import { get, post } from "aws-amplify/api";
import { fetchAuthSession } from "aws-amplify/auth";
import axios from "axios";
import {
  MOCK_BASELINES,
  MOCK_ROLLOUTS,
  MOCK_DEVICES,
  MOCK_TELEMETRY,
  MOCK_USERS,
  MOCK_SEGMENTS,
} from "./mockData";
import { isIdleExpired } from "./idleSession";

const USE_MOCK = process.env.REACT_APP_USE_MOCK_AUTH === "true";
const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT || "";
const IS_LOCAL = API_ENDPOINT.startsWith("http://localhost") || API_ENDPOINT.startsWith("http://127.");
const API_NAME = "TetraOTAApi";

// Base URL from REACT_APP_API_BASE_URL — trailing slash is normalised away for path construction.
const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");

// APIs not yet deployed — always return mock data regardless of USE_MOCK flag.
const USE_MOCK_DEVICES = false;
const USE_MOCK_ROLLOUTS = false;
const USE_MOCK_TELEMETRY = false;
const USE_MOCK_USERS = false;
const USE_MOCK_SEGMENTS = false;

const FIRMWARE_BASE = API_BASE_URL;

export function getFirmwareBinUrl(id) {
  return `${FIRMWARE_BASE}/firmware/${id}?type=bin`;
}

export function getFirmwareReleaseNotesUrl(id) {
  return `${FIRMWARE_BASE}/firmware/${id}?type=release-notes`;
}

// ── Session tokens ────────────────────────────────────────────────────────────
// Auth endpoints live on the same API Gateway as everything else, so this falls
// back to API_BASE_URL. REACT_APP_MFA_AUTH_BASE_URL only needs setting if auth is
// ever split onto a separate API. Declared here rather than further down because
// the refresh helpers below use it.
const MFA_AUTH_BASE =
  (process.env.REACT_APP_MFA_AUTH_BASE_URL || "").replace(/\/$/, "") || API_BASE_URL;

const ID_TOKEN_KEY = "mfa_id_token";
const ACCESS_TOKEN_KEY = "mfa_access_token";
const REFRESH_TOKEN_KEY = "mfa_refresh_token";

// Fired after a silent refresh swaps the stored id token, so AuthContext can
// re-derive the role from the new claims. Exported so the listener cannot drift
// from the dispatcher.
export const TOKENS_REFRESHED_EVENT = "auth:tokens-refreshed";

// Refresh this far before the token actually expires, so a request never leaves
// with a token that dies in flight and to absorb modest clock skew.
const REFRESH_SKEW_MS = 5 * 60 * 1000;

function decodeJwtExp(token) {
  try {
    const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    return typeof payload.exp === "number" ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

export function clearSessionTokens() {
  localStorage.removeItem(ID_TOKEN_KEY);
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

// Why the session ended, so the login page can say "you were signed out for
// inactivity" rather than a generic expiry. Derived rather than passed in, so
// every existing endSession() call site reports the right reason unchanged.
export const SESSION_END_REASON_KEY = "session_end_reason";

function endSession(reason) {
  const why = reason || (isIdleExpired() ? "idle" : "expired");
  // Revoke the session server-side too (TS-07-TC-0003 FT-002, DISC-004). It reads
  // the tokens before clearing below; the redirect waits for it because leaving
  // the page would cancel the call. Never rejects and is capped at 4 s.
  const revoked = recordLogout(why);
  clearSessionTokens();
  sessionStorage.setItem("session_expired", "1");
  sessionStorage.setItem(SESSION_END_REASON_KEY, why);
  localStorage.removeItem("session_expired");
  if (window.location.pathname !== "/login") {
    revoked.finally(() => { window.location.href = "/login"; });
  }
}

// A single in-flight refresh shared by every caller. Without this, a page that
// fires six requests against an expired token triggers six refreshes and Cognito
// throttles — which looks exactly like the logout bug this is meant to fix.
let refreshInFlight = null;

/**
 * Exchange the stored refresh token for a fresh id/access pair.
 * Resolves with the new id token, or null when the session cannot be recovered.
 * Throws only for transient failures, so callers can keep the current session.
 */
async function refreshTokens() {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    // FMP-AU-005: an idle session must not be revived. Every silent renewal goes
    // through here, so this is the one place the rule has to hold — the refresh
    // token is valid for days and would otherwise undo the idle timeout on the
    // user's next click. Returning null puts the caller on its existing
    // sign-out path, which reports the reason as "idle".
    if (isIdleExpired()) return null;

    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (!refreshToken) return null;   // nothing to refresh with — caller signs out

    const res = await fetch(`${MFA_AUTH_BASE}/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
    const data = await res.json().catch(() => ({}));

    if (res.ok && data.id_token) {
      localStorage.setItem(ID_TOKEN_KEY, data.id_token);
      if (data.access_token) localStorage.setItem(ACCESS_TOKEN_KEY, data.access_token);
      // With refresh token rotation on, the token just spent stops working after the
      // grace period, so the one returned must replace it or the next refresh signs
      // the user out. Absent while rotation is off; the stored one is then still good.
      if (data.refresh_token) localStorage.setItem(REFRESH_TOKEN_KEY, data.refresh_token);

      // A refreshed token can carry different cognito:groups than the one it
      // replaces — an admin may have changed this user's role mid-session. Without
      // this notification the React role stays at its mount-time value and the UI
      // keeps offering actions the API will now refuse. An event rather than an
      // imported setter keeps this module free of a React dependency. Note this
      // reaches the current tab only; a background tab keeps its stale role until
      // it refreshes on its own, which a `storage` listener on ID_TOKEN_KEY would
      // fix if that ever matters.
      window.dispatchEvent(
        new CustomEvent(TOKENS_REFRESHED_EVENT, { detail: { idToken: data.id_token } })
      );
      return data.id_token;
    }

    // The refresh token itself is dead — the session is genuinely over.
    if (res.status === 401 || data.code === "REFRESH_EXPIRED") return null;

    // Anything else (503, network, throttle) is transient. Throwing keeps the
    // existing session alive rather than signing the user out over a blip.
    throw new Error(data.message || `Refresh failed (${res.status})`);
  })().finally(() => { refreshInFlight = null; });

  return refreshInFlight;
}

/**
 * Public entry point for a session refresh. Updates the stored tokens and
 * resolves with the new id token, or null when the session cannot be recovered.
 * Throws on transient failures so the caller can keep the current session.
 */
export async function refreshSession() {
  return refreshTokens();
}

// Retrieve the ID token — checks localStorage (MFA API flow) first, then Cognito.
// Refreshes proactively rather than ending the session the moment a token expires.
export async function getAuthToken() {
  const mfaToken = localStorage.getItem(ID_TOKEN_KEY);
  if (mfaToken) {
    const expiresAt = decodeJwtExp(mfaToken);

    // Corrupt token: no usable exp. Hand it over and let the API reject it —
    // handleUnauthorizedSession will deal with the 401.
    if (expiresAt === null) return mfaToken;

    if (Date.now() < expiresAt - REFRESH_SKEW_MS) return mfaToken;

    try {
      const refreshed = await refreshTokens();
      if (refreshed) return refreshed;
      endSession();          // refresh token missing or rejected
      return null;
    } catch {
      // Transient refresh failure. Return what we have; if it is still valid the
      // request succeeds, and if not the 401 path retries the refresh.
      return mfaToken;
    }
  }
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() || null;
  } catch {
    return null;
  }
}

// FMP-AT-002: tell the server a session is ending, so the logout is recorded
// (POST /auth/logout writes USER_LOGOUT for the verified caller) and the session's
// refresh token is revoked. Never blocks or delays the sign-out: capped at 4 s in
// total and ignores any error.
//
// Every way a session ends calls this - the user signing out, the idle sweep and an
// expired session found on load - so a refresh token left in someone else's hands
// can no longer mint sessions after the portal has let go of it
// (TP-SAF-WEB-TS-07-TC-0003 FT-002, DISC-004). The tokens are read synchronously,
// before the first await, so a caller may clear storage straight after calling it.
//
// The logout call is authorised by an id token. When the stored one has expired,
// the refresh token is spent once on /auth/refresh to get a current id token for
// that one call; the new tokens are never stored, so this cannot revive the
// session (an idle one included). If the refresh is refused, the refresh token is
// already dead and there is nothing left to revoke.
export async function recordLogout(reason = "user") {
  let token = localStorage.getItem(ID_TOKEN_KEY);
  let refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY) || null;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 4000);
  try {
    if (!token) {
      try { token = (await fetchAuthSession()).tokens?.idToken?.toString() || null; } catch { token = null; }
    }
    const expiresAt = token ? decodeJwtExp(token) : null;
    if (!token || (expiresAt !== null && Date.now() >= expiresAt)) {
      const fresh = refreshToken ? await tokensForLogout(refreshToken, controller.signal) : null;
      token = fresh?.idToken || null;
      // With rotation on, the token just spent is replaced: revoke the live one.
      if (fresh?.refreshToken) refreshToken = fresh.refreshToken;
    }
    if (!token) return;
    await fetch(`${MFA_AUTH_BASE}/auth/logout`, {
      method: "POST",
      keepalive: true,
      signal: controller.signal,
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      // The server revokes this session's refresh token, so a copy taken before the
      // sign-out cannot keep minting sessions (VAPT 11.3.10).
      body: JSON.stringify({ reason, refresh_token: refreshToken || undefined }),
    });
  } catch {
    // The portal session ends regardless; the authorizer log still shows the attempt.
  } finally {
    clearTimeout(timer);
  }
}

// A current id token for the logout call only. Deliberately not refreshTokens():
// that one stores what it gets, honours the idle gate and announces the refresh,
// all of which would be wrong for a session that is being ended.
async function tokensForLogout(refreshToken, signal) {
  try {
    const res = await fetch(`${MFA_AUTH_BASE}/auth/refresh`, {
      method: "POST",
      signal,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data.id_token) return null;
    return { idToken: data.id_token, refreshToken: data.refresh_token || null };
  } catch {
    return null;
  }
}

// The access token is what the approval endpoints need (cognito.get_user rejects
// an id token). Goes through getAuthToken first so the refresh path is not
// bypassed by reading localStorage directly.
export async function getAccessToken() {
  // getAuthToken refreshes when the session is near expiry, which rewrites the
  // stored access token too — so read it only after that has had a chance to run.
  const idToken = await getAuthToken();

  const stored = localStorage.getItem(ACCESS_TOKEN_KEY);
  if (stored) return stored;

  // Only the MFA API sign-in path writes mfa_access_token. A session hydrated
  // through Amplify (the getCurrentUser/fetchAuthSession branch in
  // AuthContext.loadSession) never sets it, so ask Amplify directly rather than
  // falling through to the id token — cognito.get_user rejects id tokens, and on
  // the WebSocket $connect route that rejection surfaces only as an opaque
  // "connection failed" with no status code visible to the client.
  try {
    const session = await fetchAuthSession();
    const access = session.tokens?.accessToken?.toString();
    if (access) return access;
  } catch {
    // No Amplify session either — fall through.
  }

  // Last resort. The API will reject this, which is the honest outcome: better a
  // clean 401 than pretending we have a usable credential.
  return idToken;
}

/**
 * Handle a 401/403 from an API call by attempting one refresh.
 * Returns true when the session was ended (caller should stop).
 */
async function handleUnauthorizedSession(err) {
  const status = Number(err?.response?.status);
  if (status !== 401 && status !== 403) return false;

  // A 401 from the refresh endpoint itself must not trigger another refresh.
  const url = String(err?.config?.url || err?.response?.config?.url || "");
  if (url.includes("/auth/refresh")) {
    endSession();
    return true;
  }

  try {
    const refreshed = await refreshTokens();
    if (refreshed) return false;   // recovered — caller may retry the request
  } catch {
    // Transient: keep the session rather than signing out over a blip.
    return false;
  }

  endSession();
  return true;
}

// ── MFA Auth API ──────────────────────────────────────────────────────────────
// MFA_AUTH_BASE is declared with the session-token helpers above.

/**
 * Standard (non-MFA) web sign-in.
 * POST /web-signin with { email, password }.
 */
export async function apiWebSignIn(email, password) {
  const res = await fetch(`${MFA_AUTH_BASE}/web-signin`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message || `Sign-in failed (${res.status})`);
  return data;
}

/**
 * Step 1 – Initiate sign-in with username + password.
 * Returns either tokens (isSignedIn) or a challenge (nextStep + session).
 */
export async function apiMfaSignIn(username, password) {
  const res = await fetch(`${MFA_AUTH_BASE}/auth/mfa/signin`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email: username, password }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message || `Sign-in failed (${res.status})`);
  return data;
}

/**
 * Step 2a – Confirm sign-in with TOTP/SMS OTP code.
 * Sends email, session (from step 1), and the entered OTP.
 */
export async function apiMfaSignInConfirm(email, session, otpCode) {
  const res = await fetch(`${MFA_AUTH_BASE}/auth/mfa/signin/confirm`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ otp_code: otpCode, session, email }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message || `OTP verification failed (${res.status})`);
  return data;
}

/**
 * Step 2 – Set a new password after NEW_PASSWORD_REQUIRED challenge.
 * Returns { next_step, access_token, id_token, ... }.
 */
export async function apiNewPassword(email, newPassword, session) {
  const res = await fetch(`${MFA_AUTH_BASE}/auth/new-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, new_password: newPassword, session }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message || `Set password failed (${res.status})`);
  return data;
}

/**
 * Step 3 – Fetch QR code / secret for first-time TOTP setup.
 * Requires the access_token returned by /auth/new-password.
 */
export async function apiMfaSetup(accessToken) {
  const res = await fetch(`${MFA_AUTH_BASE}/auth/mfa/setup`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${accessToken}`,
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message || `MFA setup failed (${res.status})`);
  return data;
}

/**
 * Step 4 – Verify the TOTP code to complete MFA enrollment.
 * Requires the access_token returned by /auth/new-password.
 */
export async function apiMfaVerify(accessToken, otpCode) {
  const res = await fetch(`${MFA_AUTH_BASE}/auth/mfa/verify`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ otp_code: otpCode }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message || `MFA verification failed (${res.status})`);
  return data;
}

async function authHeaders(extra = {}) {
  const token = await getAuthToken();
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
}

// Plain fetch with JWT authorization
async function localGet(path) {
  const headers = await authHeaders();
  const res = await fetch(`${API_ENDPOINT}${path}`, { headers });
  if (!res.ok) throw new Error(`GET ${path} → ${res.status}`);
  return res.json();
}

async function localPost(path, payload) {
  const headers = await authHeaders({ "Content-Type": "application/json" });
  const res = await fetch(`${API_ENDPOINT}${path}`, {
    method: "POST",
    headers,
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`POST ${path} → ${res.status}`);
  return res.json();
}

// Amplify (AWS API Gateway + Cognito) for production
async function amplifyGet(path) {
  const { body } = await get({ apiName: API_NAME, path }).response;
  return body.json();
}

async function amplifyPost(path, payload) {
  const { body } = await post({ apiName: API_NAME, path, options: { body: payload } }).response;
  return body.json();
}

// Plain fetch against API_BASE_URL with JWT (used when not running locally)
async function baseGet(path) {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE_URL}${path}`, { headers });
  if (!res.ok) throw new Error(`GET ${path} → ${res.status}`);
  return res.json();
}

async function basePost(path, payload) {
  const headers = await authHeaders({ "Content-Type": "application/json" });
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method: "POST",
    headers,
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`POST ${path} → ${res.status}`);
  return res.json();
}

function apiGet(path) { return IS_LOCAL ? localGet(path) : API_BASE_URL ? baseGet(path) : amplifyGet(path); }
function apiPost(path, body) { return IS_LOCAL ? localPost(path, body) : API_BASE_URL ? basePost(path, body) : amplifyPost(path, body); }
// ── Firmware file upload (Step 1 — file bytes only) ──────────────────────────
const UPLOAD_MAX_ATTEMPTS = 3;
const UPLOAD_RETRY_DELAYS_MS = [2000, 5000]; // backoff before attempts 2 and 3
const UPLOAD_TIMEOUT_MS = 120000;

// Network-level failures (no response reached us) and transient server errors are
// worth another attempt. A 4xx — 413 too large, 400 bad payload, 401 expired — will
// fail identically every time, so retrying only delays the error the user needs.
function isRetryableUploadError(err) {
  if (err?.status == null) return true;
  return err.status === 429 || err.status >= 500;
}

// One attempt. Rejects with an Error carrying `.status` when the server responded.
function sendFirmwareUpload({ uploadUrl, body, token, onProgress, onXhr }) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    if (typeof onXhr === "function") onXhr(xhr);
    xhr.open("POST", uploadUrl);
    xhr.timeout = UPLOAD_TIMEOUT_MS;
    xhr.setRequestHeader("Content-Type", "application/json");
    if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && typeof onProgress === "function") {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try { resolve(JSON.parse(xhr.responseText)); } catch { resolve({}); }
      } else {
        const err = new Error(`Upload failed: ${xhr.status} ${xhr.statusText}`);
        err.status = xhr.status;
        reject(err);
      }
    };
    xhr.onerror = () => reject(new Error("Network error during upload."));
    xhr.ontimeout = () => reject(new Error("Upload timed out."));
    xhr.onabort = () => reject(new Error("Upload aborted."));
    xhr.send(body);
  });
}

// The service accepts at most this many reported failures (MAX_TRANSFER_FAILURES in
// upload-firmware-lambda); only the most recent are kept so repeated manual retries
// can never push a good upload over the limit.
const MAX_REPORTED_TRANSFER_FAILURES = 20;

// A failed attempt as the service records it: when, which attempt, and a fixed
// reason - never the raw error text.
function transferFailure(attempt, err) {
  let reason = "network error";
  if (err?.status === 429) reason = "rate limited";
  else if (err?.status >= 500) reason = "server error";
  else if (/timed out/i.test(err?.message || "")) reason = "timed out";
  return { attempt, at: new Date().toISOString(), reason, status: err?.status ?? null };
}

// priorFailures: failed attempts from earlier tries of the same file (the Retry
// Upload button). A retry happens in the browser and a failed attempt usually never
// reaches the service, so the failures are sent with the attempt that gets through
// and recorded there (FIRMWARE_UPLOAD_RETRIED, TP-FMP-02-TS-02-TC-0013 FT-006). When
// every attempt fails, the thrown error carries them as `transferFailures`.
export async function uploadFirmwareFile({ file, fileName, onProgress, onRetry, name, email, buildDate, buildTime, sha256, priorFailures = [] }) {
  const uploadUrl = `${API_BASE_URL}/upload`;
  if (!API_BASE_URL || !file) throw new Error("Upload URL or file not configured.");

  // Encoded once and reused across attempts — re-reading the file per retry would
  // cost a full extra read + base64 pass every time.
  const base64 = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  // build_time: combine date + time into full ISO-8601 UTC string
  const buildTimeIso = buildDate && buildTime
    ? new Date(`${buildDate}T${buildTime}:00Z`).toISOString()
    : new Date().toISOString();

  const payload = {
    file_name: fileName || file.name,
    file_data: base64,
    ...(name ? { name } : {}),
    ...(email ? { email } : {}),
    ...(sha256 ? { sha256 } : {}),
    build_date: buildDate || new Date().toISOString().slice(0, 10),
    build_time: buildTimeIso,
  };
  const body = JSON.stringify(payload);
  // No failures: exactly the request sent before this was added.
  const bodyWith = (failures) => (failures.length
    ? JSON.stringify({ ...payload, transfer_failures: failures.slice(-MAX_REPORTED_TRANSFER_FAILURES) })
    : body);

  const failures = [...priorFailures];
  let lastError;
  for (let attempt = 1; attempt <= UPLOAD_MAX_ATTEMPTS; attempt += 1) {
    if (typeof onProgress === "function") onProgress(0);
    try {
      // Fetched per attempt: a backoff wait can cross a token refresh, and
      // getAuthToken() also handles session expiry.
      const token = await getAuthToken();
      return await sendFirmwareUpload({ uploadUrl, body: bodyWith(failures), token, onProgress });
    } catch (err) {
      lastError = err;
      // Only transfer faults are reported; a 4xx reached the service, which
      // records that refusal itself.
      if (isRetryableUploadError(err)) failures.push(transferFailure(attempt, err));
      const canRetry = attempt < UPLOAD_MAX_ATTEMPTS && isRetryableUploadError(err);
      if (!canRetry) break;
      if (typeof onRetry === "function") {
        onRetry({ attempt: attempt + 1, maxAttempts: UPLOAD_MAX_ATTEMPTS, reason: err.message });
      }
      await new Promise((r) => setTimeout(r, UPLOAD_RETRY_DELAYS_MS[attempt - 1] ?? 5000));
    }
  }
  const error = lastError || new Error("Upload failed.");
  error.transferFailures = failures.slice(-MAX_REPORTED_TRANSFER_FAILURES);
  throw error;
}

// ── Baselines ──────────────────────────────────────────────────────────────────
function normaliseBaseline(b) {
  return {
    ...b,
    size: b.size ?? b.file_size ?? null,
    publishedAt: b.publishedAt ?? b.published_at ?? null,
    uploadedAt: b.uploadedAt ?? b.uploaded_at ?? null,
    mandatory: b.mandatory ?? (b.type === "Mandatory") ?? false,
    // FMP-DP-001: devices running this version now, from the service. null means the
    // service could not count them - shown as "count unavailable", never as 0 (DISC-033).
    devicesUpToDate: b.devicesUpToDate ?? b.devices_on_version ?? null,
    state: (() => {
      const s = (b.state || "").toLowerCase();
      if (s === "published") return "PUBLISHED";
      if (s === "rollout") return "ROLLOUT";
      if (s === "rollback") return "ROLLBACK";
      if (s === "paused") return "PAUSED";
      if (s === "approved") return "APPROVED";
      if (s === "signed") return "SIGNED";
      if (s === "deprecated") return "DEPRECATED";
      if (s === "pending") return "PENDING";
      return "DRAFT";
    })(),
  };
}

export async function getBaselines({ page = 1, limit = 10, search = "", state = "", sort = "", order = "" } = {}) {
  if (USE_MOCK && !API_BASE_URL) {
    return { items: MOCK_BASELINES, total: MOCK_BASELINES.length, page: 1, limit, totalPages: 1 };
  }
  // search, state, sort and order are only sent when set, so callers that pass
  // none of them get exactly the previous request. Sorting is done by the service
  // before it paginates (FMP-BL-001), never on the page in hand.
  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (search) params.set("search", search);
  if (state && state.toLowerCase() !== "all") params.set("state", state.toLowerCase());
  if (sort) params.set("sort", sort);
  if (order) params.set("order", order);

  const headers = await authHeaders();
  const res = await fetch(`${FIRMWARE_BASE}/firmware/baselines?${params.toString()}`, { headers });
  if (!res.ok) throw new Error(`GET /firmware/baselines \u2192 ${res.status}`);
  const data = await res.json();
  const rawItems = Array.isArray(data)
    ? data
    : data.baselines ?? data.items ?? data.firmware ?? data.data ?? [];
  return {
    items: rawItems.map(normaliseBaseline),
    total: data.total ?? rawItems.length,
    page: data.page ?? page,
    limit: data.limit ?? limit,
    totalPages: data.total_pages ?? Math.ceil((data.total ?? rawItems.length) / limit),
  };
}

export async function getBaseline(id, { view = "" } = {}) {
  if (USE_MOCK && !API_BASE_URL) return MOCK_BASELINES.find((b) => b.id === id) || null;
  const headers = await authHeaders();
  // view labels the service's audit entry (e.g. "history" from a DEPRECATED row,
  // TC-0011). Informational only; the record returned is the same.
  const query = view ? `?view=${encodeURIComponent(view)}` : "";
  const res = await fetch(`${FIRMWARE_BASE}/firmware/${id}${query}`, { headers });
  if (!res.ok) throw new Error(`GET /firmware/${id} \u2192 ${res.status}`);
  return res.json();
}

export async function getFirmwarePublishDetails(id) {
  const headers = await authHeaders();
  const res = await fetch(`${FIRMWARE_BASE}/firmware/${id}/publish-details`, { headers });
  if (!res.ok) throw new Error(`GET /firmware/${id}/publish-details \u2192 ${res.status}`);
  return res.json();
}

export async function getFirmwareFileInfo(id) {
  const headers = await authHeaders();
  const res = await fetch(`${FIRMWARE_BASE}/firmware/${id}/getdate`, { headers });
  if (!res.ok) throw new Error(`GET /firmware/${id}/getdate \u2192 ${res.status}`);
  return res.json();
}

export async function uploadBaseline({ file, fileName, onProgress, ...meta }) {
  const uploadUrl = `${API_BASE_URL}/upload`;
  console.log("[uploadBaseline] uploadUrl:", uploadUrl);
  console.log("[uploadBaseline] file:", file);
  // If a real base URL is configured, always call it (even in mock mode)
  if (API_BASE_URL && file) {
    console.log("[uploadBaseline] Reading file as base64...");
    const base64 = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(",")[1]); // strip data:...;base64,
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
    const payload = {
      file_name: fileName || file.name,
      file_data: base64,
      version_string: meta.version,
      min_device_fw_required: meta.minFw || null,
      release_notes: meta.releaseNotes || null,
      is_mandatory: meta.mandatory ?? false,
      target_segment: meta.segment || null,
    };
    console.log("[uploadBaseline] POSTing to:", uploadUrl, "file_name:", payload.file_name);
    const body = JSON.stringify(payload);
    const token = await getAuthToken();
    const result = await new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", uploadUrl);
      xhr.setRequestHeader("Content-Type", "application/json");
      if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && typeof onProgress === "function") {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try { resolve(JSON.parse(xhr.responseText)); } catch { resolve({}); }
        } else {
          reject(new Error(`Upload failed: ${xhr.status} ${xhr.statusText}`));
        }
      };
      xhr.onerror = () => reject(new Error("Network error during upload."));
      xhr.send(body);
    });
    console.log("[uploadBaseline] Upload complete.");
    return result;
  }
  console.warn("[uploadBaseline] SKIPPED real API — uploadUrl:", uploadUrl, "file:", file);
  // Fallback: mock mode
  if (USE_MOCK) return { id: "fw-new-" + Date.now(), ...meta, fileName, state: "DRAFT" };
  return amplifyPost("/firmware", { fileName, ...meta });
}

// ── Approval / electronic signature (21 CFR Part 11) ─────────────────────────
// The backend verifies the caller with cognito.get_user, which requires the
// ACCESS token — the id token used elsewhere will not work here.
function approvalAuthHeaders(token) {
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

export async function approveBaseline(id, statementText) {
  if (USE_MOCK) return { id, state: "APPROVED" };
  try {
    const token = await getAccessToken();
    const response = await axios.post(
      `${FIRMWARE_BASE}/firmware/${id}/approve`,
      { statement_text: statementText },
      { headers: approvalAuthHeaders(token) }
    );
    return response?.data ?? {};
  } catch (err) {
    // 401 and 403 both have to be handled here now. The gateway used to answer
    // an expired token with 401 and a role denial with 403, so the status alone
    // told them apart. The role authorizer is a Lambda authorizer returning a
    // simple response, and API Gateway renders every such denial as 403 — so an
    // expired session and a forbidden role arrive looking identical.
    //
    // Passing both to handleUnauthorizedSession is still safe for a role denial:
    // it tries a refresh first and only ends the session if that refresh comes
    // back empty. A live session survives; a dead one signs out as it should.
    handleUnauthorizedSession(err);
    const message = err?.response?.data?.message || err?.message || "Failed to record approval.";
    throw new Error(message);
  }
}

export async function getFirmwareApprovals(id) {
  try {
    const token = await getAccessToken();
    const response = await axios.get(
      `${FIRMWARE_BASE}/firmware/${id}/approvals`,
      { headers: approvalAuthHeaders(token) }
    );
    return response?.data ?? {};
  } catch (err) {
    // 403 as well as 401 — see the note in apiApproveFirmware above.
    handleUnauthorizedSession(err);
    const message = err?.response?.data?.message || err?.message || "Failed to load approval records.";
    throw new Error(message);
  }
}

// ── User Settings ─────────────────────────────────────────────────────────────
export async function getUserSettings() {
  const token = await getAccessToken();
  const res = await fetch(`${MFA_AUTH_BASE}/user/user-settings-page`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message || `Failed to load settings (${res.status})`);
  return data;
}

export async function updateUserSettings(payload) {
  const token = await getAccessToken();
  const res = await fetch(`${MFA_AUTH_BASE}/user/settings-update`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message || `Failed to update settings (${res.status})`);
  return data;
}

export async function deprecateBaseline(id, reason) {
  const headers = await authHeaders({ "Content-Type": "application/json" });
  const res = await fetch(`${FIRMWARE_BASE}/firmware/${id}/deprecate`, {
    method: "PATCH",
    headers,
    body: JSON.stringify({ deprecation_reason: reason }),
  });
  if (!res.ok) {
    const errText = await res.text().catch(() => "");
    const err = new Error(`PATCH /firmware/${id}/deprecate \u2192 ${res.status}${errText ? ": " + errText : ""}`);
    // The status travels with the error. A 403 here is refused by the
    // AUTHORIZER, before deprecate-firmware runs, so the only text the backend
    // returns is API Gateway's bare "Forbidden" - a caller needs the status to
    // say anything more useful than that.
    err.status = res.status;
    err.body = errText;
    throw err;
  }
  const text = await res.text();
  return text ? JSON.parse(text) : {};
}

// Pause / resume a firmware rollout (rollout-pause-resume). Resume is the only way
// a rollout the canary breaker paused goes back to delivering; the service
// requires a reason for it and records it in the audit trail.
async function postRolloutAction(id, action, reason) {
  const headers = await authHeaders({ "Content-Type": "application/json" });
  const res = await fetch(`${FIRMWARE_BASE}/firmware/${id}/${action}`, {
    method: "POST",
    headers,
    body: JSON.stringify(reason ? { reason } : {}),
  });
  if (!res.ok) {
    const errText = await res.text().catch(() => "");
    const err = apiError(`POST /firmware/${id}/${action}`, res, errText);
    console.error(`[${action}FirmwareRollout] ${err.technical}`);
    throw err;
  }
  const text = await res.text();
  return text ? JSON.parse(text) : {};
}

export function pauseFirmwareRollout(id, reason = "") {
  return postRolloutAction(id, "pause", reason);
}

export function resumeFirmwareRollout(id, reason) {
  return postRolloutAction(id, "resume", reason);
}

// FMP-DP-004: a cancelled deprecation is audit-logged like a confirmed one
// (DISC-043). Same route; the service changes nothing and records
// FIRMWARE_DEPRECATION_CANCELLED against the verified caller. keepalive lets the
// call finish even if the operator navigates away straight after cancelling.
export async function cancelDeprecation(id) {
  const headers = await authHeaders({ "Content-Type": "application/json" });
  const res = await fetch(`${FIRMWARE_BASE}/firmware/${id}/deprecate`, {
    method: "PATCH",
    headers,
    body: JSON.stringify({ action: "cancel" }),
    keepalive: true,
  });
  if (!res.ok) {
    const errText = await res.text().catch(() => "");
    throw new Error(`PATCH /firmware/${id}/deprecate (cancel) → ${res.status}${errText ? ": " + errText : ""}`);
  }
  return res.json().catch(() => ({}));
}

// The service already returns a message written to be read by the operator -
// "Version '2.0.32.63' is already used by baseline fbe790ec...", say. Surfacing
// the raw "PATCH /firmware/{id}/metadata -> 409: {"message":"..."}" string put
// the method, path, status code and a JSON envelope in front of the user and
// buried the one sentence that actually told them what to do. This pulls that
// sentence out, and keeps the technical form on the error for the console.
function apiError(label, res, errText) {
  let message = "";
  try {
    message = (JSON.parse(errText) || {}).message || "";
  } catch (_) {
    message = "";
  }
  if (!message) {
    // No usable body: say something the operator can act on rather than
    // showing them a bare status code.
    if (res.status === 401) message = "Your session has expired. Sign in again to continue.";
    else if (res.status === 403) message = "Your role does not permit this action.";
    else if (res.status === 404) message = "That baseline no longer exists.";
    else if (res.status >= 500) message = "The service could not complete this request. Please try again, and contact your administrator if it continues.";
    else message = "The request could not be completed.";
  }
  const err = new Error(message);
  err.status = res.status;
  err.technical = `${label} \u2192 ${res.status}${errText ? ": " + errText : ""}`;
  return err;
}

export async function patchFirmwareMetadata(id, meta) {
  if (USE_MOCK && !API_BASE_URL) return { id, ...meta };
  const headers = await authHeaders({ "Content-Type": "application/json" });
  const res = await fetch(`${FIRMWARE_BASE}/firmware/${id}/metadata`, {
    method: "PATCH",
    headers,
    body: JSON.stringify(meta),
  });
  if (!res.ok) {
    const errText = await res.text().catch(() => "");
    const err = apiError(`PATCH /firmware/${id}/metadata`, res, errText);
    console.error(`[patchFirmwareMetadata] ${err.technical}`);
    throw err;
  }
  const text = await res.text();
  return text ? JSON.parse(text) : {};
}

export async function finalPublishFirmware(id, rolloutTier) {
  // Publisher identity is taken from the token server-side, so nothing about the
  // user is sent in the body. The rollout tier is sent because it is the
  // operator's choice: it caps how many devices the rollout may target. The
  // service validates it against the permitted set and persists it.
  const token = await getAccessToken();
  const res = await fetch(`${FIRMWARE_BASE}/firmware/${id}/finalpublish`, {
    method: "PATCH",
    headers: approvalAuthHeaders(token),
    body: JSON.stringify({ rollout_tier: rolloutTier }),
  });
  if (!res.ok) {
    const errText = await res.text().catch(() => "");
    // Step-up (TP-SAF-WEB-TS-07-TC-0003 FT-004): the session is older than the
    // re-auth window. Flagged so the caller can ask for the credentials again and
    // retry, rather than showing a failure or treating it as an expired session.
    let data = {};
    try { data = JSON.parse(errText); } catch { data = {}; }
    if (res.status === 401 && data.code === "REAUTH_REQUIRED") {
      const err = new Error(data.message || "Re-authentication required to publish.");
      err.reauthRequired = true;
      err.maxAgeSeconds = data.max_age_seconds;
      throw err;
    }
    throw new Error(`PATCH /firmware/${id}/finalpublish \u2192 ${res.status}${errText ? ": " + errText : ""}`);
  }
  const text = await res.text();
  return text ? JSON.parse(text) : {};
}

export async function forgotPasswordInitiation(email) {
  const res = await fetch(`${MFA_AUTH_BASE}/auth/forgot-password-initiation`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) {
    const errText = await res.text().catch(() => "");
    throw new Error(`POST /auth/forgot-password-initiation → ${res.status}${errText ? ": " + errText : ""}`);
  }
  const text = await res.text();
  return text ? JSON.parse(text) : {};
}

// Completes a reset with the one-time token from the e-mailed link
// (TP-SAF-WEB-TS-07-TC-0002). The service's message is shown as-is on failure.
export async function forgotPasswordConfirm(token, new_password) {
  const res = await fetch(`${MFA_AUTH_BASE}/auth/forgot-password/confirm`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, new_password }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const detail = data.requirements ? ` ${data.requirements}` : "";
    throw new Error((data.message || `Password reset failed (${res.status})`) + detail);
  }
  return data;
}

// The rollout a firmware is currently running: its cohort per country with outcome
// counts, the tier, the cap and the headroom left under it. `widening` carries the
// service's own answer on whether more devices may be added right now - rendered as
// given rather than re-derived here, so the portal and the service cannot disagree
// about why an operator is blocked (SRS FMP-NF-003: the browser is presentational).
export async function getRolloutCohort(firmwareId) {
  const token = await getAccessToken();
  const response = await axios.get(
    `${MFA_AUTH_BASE}/rollout-countrywise-device-mapping`,
    {
      params: { firmware_id: firmwareId },
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    }
  );
  return response?.data ?? {};
}

// rollout_tier is optional and sent only when the operator is moving the rollout to
// a wider tier. The service applies the tier and the added devices in ONE request,
// validating both before writing either - so a selection that is refused cannot
// leave the baseline widened on its way to the rejection.
export async function postRolloutCountrywiseDeviceMapping({ firmware_id, country = [], serial_nos = [], rollout_tier } = {}) {
  const token = await getAccessToken();
  const payload = {
    firmware_id,
    country: Array.isArray(country) ? country.filter(Boolean) : [],
    serial_nos: Array.isArray(serial_nos) ? serial_nos.filter(Boolean) : [],
    ...(rollout_tier ? { rollout_tier } : {}),
  };

  const response = await axios.post(
    `${MFA_AUTH_BASE}/rollout-countrywise-device-mapping`,
    payload,
    {
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    }
  );

  return response?.data ?? {};
}

/* RETIRED. POST /firmware/{id}/publish performed no role check, no
   re-authentication and no approval check - it published whatever record it was
   handed, which walked around the two-person rule entirely
   (TP-SAF-WEB-TS-02-TC-0001 FT-003). The service side now answers 410 and records
   FIRMWARE_PUBLISH_REFUSED.

   Kept as a throwing stub rather than deleted so that a call site resurrected from
   an old branch fails loudly here, at build-or-first-call, instead of silently
   reaching a route that no longer publishes. Use finalPublishFirmware. */
export async function publishBaseline() {
  throw new Error(
    "publishBaseline is retired: POST /firmware/{id}/publish bypassed the approval " +
    "gate and now answers 410. Use finalPublishFirmware(id, rolloutTier)."
  );
}

// ── Rollouts ───────────────────────────────────────────────────────────────────
export async function getRollouts() {
  if (USE_MOCK || USE_MOCK_ROLLOUTS) return MOCK_ROLLOUTS;
  return apiGet("/rollouts");
}

export async function getRollout(id) {
  if (USE_MOCK || USE_MOCK_ROLLOUTS) return MOCK_ROLLOUTS.find((r) => r.id === id) || null;
  return apiGet(`/rollouts/${id}`);
}

export async function pauseRollout(id) {
  if (USE_MOCK) return { id, state: "PAUSED" };
  return apiPost(`/rollouts/${id}/pause`, {});
}

export async function resumeRollout(id) {
  if (USE_MOCK) return { id, state: "CANARY" };
  return apiPost(`/rollouts/${id}/resume`, {});
}

// exportFormat ("csv" | "json" | "pdf") marks the call as an export: the service
// audits it as REPORT_EXPORTED, and the Export screen builds its file from this very
// response (TP-FMP-05-DISC-016). Omitted, the call is the plain view it always was.
export async function getCurrentBaselineRolloutSummary(country = "", from_date = "", to_date = "", exportFormat = "") {
  try {
    const token = await getAccessToken();
    const params = new URLSearchParams();
    if (country) params.append("country", country);
    if (from_date) params.append("from_date", from_date);
    if (to_date) params.append("to_date", to_date);
    if (exportFormat) params.append("export", exportFormat);

    const url = `${MFA_AUTH_BASE}/reports/rollout-summary/Current-Baseline-view${params.toString() ? "?" + params.toString() : ""}`;
    const response = await axios.get(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return response?.data ?? {};
  } catch (err) {
    handleUnauthorizedSession(err);
    const message = err?.response?.data?.message || err?.message || "Failed to fetch current baseline rollout summary.";
    throw new Error(message);
  }
}

export async function getPreviousActiveRolloutSummary({ exportFormat = "" } = {}) {
  try {
    const token = await getAccessToken();
    const response = await axios.get(`${MFA_AUTH_BASE}/reports/rollout-summary/previous-active`, {
      params: exportFormat ? { export: exportFormat } : undefined,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return response?.data ?? {};
  } catch (err) {
    handleUnauthorizedSession(err);
    const message = err?.response?.data?.message || err?.message || "Failed to fetch previous active rollout summary.";
    throw new Error(message);
  }
}

export async function getDeprecatedFirmwareSummary({ exportFormat = "" } = {}) {
  try {
    const token = await getAccessToken();
    const response = await axios.get(`${MFA_AUTH_BASE}/reports/rollout-summary/deprecated-firmware`, {
      params: exportFormat ? { export: exportFormat } : undefined,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return response?.data ?? {};
  } catch (err) {
    handleUnauthorizedSession(err);
    const message = err?.response?.data?.message || err?.message || "Failed to fetch deprecated firmware summary.";
    throw new Error(message);
  }
}

// ── Devices ────────────────────────────────────────────────────────────────────
// Maps the backend's compliance status onto a badge. Only exact, known values are
// accepted: a status that is missing, of the wrong type or not recognised is shown
// as UNKNOWN - never as UP_TO_DATE, and never as MANDATORY on a partial word match
// such as "Not Required" (TP-FMP-04-TS-01-TC-0009).
const DEVICE_STATUS_MAP = {
  "UP TO DATE": "UP_TO_DATE", "UP_TO_DATE": "UP_TO_DATE", "UP-TO-DATE": "UP_TO_DATE", "UPTODATE": "UP_TO_DATE",
  "UPDATE REQUIRED": "MANDATORY", "UPDATE_REQUIRED": "MANDATORY", "MANDATORY": "MANDATORY",
  "PENDING": "PENDING",
  "FAILED": "FAILED_ROLLBACK", "ROLLBACK": "FAILED_ROLLBACK", "FAILED_ROLLBACK": "FAILED_ROLLBACK", "FAILED - ROLLBACK": "FAILED_ROLLBACK",
  "UNKNOWN": "UNKNOWN",
};
function normaliseDeviceStatus(raw) {
  if (typeof raw !== "string") return "UNKNOWN";
  return DEVICE_STATUS_MAP[raw.trim().toUpperCase()] || "UNKNOWN";
}

// With a serial, asks the service for that one device (so a device beyond the
// first page is found, and a serial that does not exist returns []); without
// one, the first page of the fleet as before.
export async function getDevices(serial) {
  if (USE_MOCK || USE_MOCK_DEVICES) return MOCK_DEVICES;

  const headers = await authHeaders();
  const query = serial ? `?serial_number=${encodeURIComponent(serial)}` : "";
  const res = await fetch(`${MFA_AUTH_BASE}/fleetoverview/listdevices${query}`, { headers });
  if (serial && res.status === 404) return [];
  if (!res.ok) throw new Error(`GET /fleetoverview/listdevices → ${res.status}`);

  const data = await res.json().catch(() => ({}));
  const records = Array.isArray(data)
    ? data
    : (serial && data?.serial_number ? [data] : (data?.devices ?? data?.fleets ?? data?.items ?? data?.data ?? []));

  return normaliseDevices(records);
}

// One device by serial, for the Device Detail page. listdevices without
// parameters returns only its first page (10 devices), so "not in that list"
// says nothing about whether a device exists. ?serial_number= is a direct
// lookup that answers 404 only when the serial is not registered - the one
// answer that justifies "Device not found". Resolves to { device, notFound };
// notFound is true only on that authoritative answer, never on a failure or on a
// service that replied with a page instead of the single record.
export async function getDeviceBySerial(serial) {
  if (USE_MOCK || USE_MOCK_DEVICES) {
    const device = MOCK_DEVICES.find((d) => d.serial === serial) || null;
    return { device, notFound: !device };
  }

  const headers = await authHeaders();
  const res = await fetch(
    `${MFA_AUTH_BASE}/fleetoverview/listdevices?serial_number=${encodeURIComponent(serial)}`,
    { headers }
  );
  if (res.status === 404) return { device: null, notFound: true };
  if (!res.ok) throw new Error(`GET /fleetoverview/listdevices?serial_number → ${res.status}`);

  const data = await res.json().catch(() => ({}));
  const listShaped = Array.isArray(data) || Array.isArray(data?.devices) || Array.isArray(data?.fleets)
    || Array.isArray(data?.items) || Array.isArray(data?.data);
  const records = listShaped
    ? (Array.isArray(data) ? data : (data.devices ?? data.fleets ?? data.items ?? data.data))
    : (data && typeof data === "object" && Object.keys(data).length ? [data] : []);
  const device = normaliseDevices(records).find((d) => d.serial === serial) || null;
  return { device, notFound: false };
}

// Keep UI stable even if backend field names vary by environment.
function normaliseDevices(records) {
  return records.map((d, index) => {
    const currentFw = d.currentFw || d.current_fw || d.current_fw_version || d.currentVersion || d.current_version || "-";
    const requiredFw = d.requiredFw ?? d.required_fw ?? d.required_fw_version ?? d.targetVersion ?? d.target_version ?? null;
    const status = normaliseDeviceStatus(
      d.status ?? d.updateStatus ?? d.update_status ?? d.deviceStatus ?? d.device_status ??
      d.fwStatus ?? d.fw_status ?? d.updateState ?? d.update_state
    );

    const serial = d.serial || d.serial_number || d.serialNo || d.serial_no || d.deviceSerial || d.device_serial || d.id || `device-${index + 1}`;

    return {
      id: d.id || d.deviceId || d.device_id || serial,
      serial,
      model: d.model || d.deviceModel || d.device_model || d.type || "Unknown",
      currentFw,
      requiredFw,
      status,
      // No fallback: a device that has never reported has no last-sync time,
      // and substituting "now" made it look freshly synced (DISC-001).
      lastUpdated: d.lastUpdated || d.last_updated || d.updatedAt || d.updated_at || null,
      segment: d.segment || d.segmentId || d.segment_id || null,
      segmentName: d.segmentName || d.segment_name || d.fleetName || d.fleet_name || null,
      currentCountry: d.current_country || d.currentCountry || null,
      baseCountry: d.base_country || d.baseCountry || null,
      name: d.name || null,
      agingDays: d.agingDays ?? d.aging_days ?? d.age_days ?? null,
      isMandatory: d.is_mandatory ?? d.isMandatory ?? false,
      registeredAt: d.registered_at || d.registeredAt || null,
      // FMP-AN-009: on a stale record `status` is the last known one, not current.
      stale: d.stale === true,
      lastReportedAt: d.last_reported_at ?? null,
      dataAgeDays: d.data_age_days ?? null,
    };
  });
}

// ── OTA update logs (before / after a firmware update) ──────────────────────
// GET /device/ota-logs serves both shapes from one route: without log_id it
// lists a device's OTA logs (metadata only), with log_id it returns that one
// log's contents. The device files them via POST /device/ota-logs, once before
// the update starts and once after it finishes.

// The two logs recorded around one update. updateId is optional; without it the
// whole device's OTA history comes back. An empty list is a normal answer - a
// device updated before this feature shipped has none.
export async function getDeviceOtaLogs(serialNumber, updateId) {
  const headers = await authHeaders();
  const params = new URLSearchParams({ serial_number: serialNumber });
  if (updateId) params.set("update_id", updateId);
  const url = `${MFA_AUTH_BASE}/device/ota-logs?${params.toString()}`;
  const res = await fetch(url, { headers });
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    console.error("[OTA Logs] Error:", res.status, body);
    if (res.status === 404) return [];
    throw new Error(`GET /device/ota-logs → ${res.status}`);
  }
  const data = await res.json().catch(() => ({}));
  const records = Array.isArray(data) ? data : (data?.logs ?? []);
  return records.map((r) => ({
    logId: r.log_id || "",
    updateId: r.update_id || null,
    phase: (r.phase || "").toLowerCase(),
    fwVersion: r.fw_version || "",
    deviceBaseline: r.device_baseline || "",
    loggedAt: r.logged_at || "",
    receivedTime: r.received_time || "",
  }));
}

// One log's contents. The service parses the device's CSV export into events;
// anything else comes back as raw text, so an unexpected format is still
// readable rather than rendering as an empty table.
export async function getDeviceOtaLogView(serialNumber, logId) {
  const headers = await authHeaders();
  const params = new URLSearchParams({ serial_number: serialNumber, log_id: logId });
  const url = `${MFA_AUTH_BASE}/device/ota-logs?${params.toString()}`;
  const res = await fetch(url, { headers });
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    console.error("[OTA Log View] Error:", res.status, body);
    throw new Error(`GET /device/ota-logs (log_id) → ${res.status}`);
  }
  const data = await res.json().catch(() => ({}));
  return {
    logId: data.log_id || logId,
    phase: (data.phase || "").toLowerCase(),
    updateId: data.update_id || null,
    fwVersion: data.fw_version || "",
    deviceBaseline: data.device_baseline || "",
    loggedAt: data.logged_at || "",
    receivedTime: data.received_time || "",
    format: data.format === "text" ? "text" : "events",
    content: data.content || "",
    events: Array.isArray(data.events) ? data.events : [],
  };
}

export async function getDeviceFirmwareHistory(serialNumber, { year, quarter } = {}) {
  const headers = await authHeaders();
  const params = new URLSearchParams({ serial_number: serialNumber });
  if (year) params.set("year", year);
  if (quarter) params.set("quarter", quarter);
  const url = `${MFA_AUTH_BASE}/device/firmware/history?${params.toString()}`;
  console.log("[FW History] Fetching:", url, "| Auth:", !!headers.Authorization);
  const res = await fetch(url, { headers });
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    console.error("[FW History] Error:", res.status, body);
    if (res.status === 404) return []; // No history for this device
    throw new Error(`GET /device/firmware/history → ${res.status}`);
  }
  const data = await res.json().catch(() => ({}));
  console.log("[FW History] Response:", data);
  // Normalise to array of { version, date, outcome }
  const records = Array.isArray(data)
    ? data
    : (data?.history ?? data?.firmware_history ?? data?.items ?? data?.data ?? []);
  return records.map((r) => {
    const rawEvent = (r.event || r.outcome || r.status || r.result || "").toString().trim().toLowerCase();
    let outcome;
    if (rawEvent === "firmware_updated" || rawEvent === "success") outcome = "SUCCESS";
    else if (rawEvent === "registered") outcome = "REGISTERED";
    else if (rawEvent.includes("rollback")) outcome = "ROLLBACK";
    else if (rawEvent.includes("fail")) outcome = "FAIL";
    else if (rawEvent.includes("pending")) outcome = "PENDING";
    // No recorded outcome is shown as unknown (grey), never as a success (DISC-013).
    else outcome = rawEvent.toUpperCase() || "UNKNOWN";
    return {
      version: r.version || r.fw_version || r.firmware_version || r.firmwareVersion || "-",
      date: r.date || r.updated_at || r.timestamp || r.updatedAt || "-",
      update_id: r.update_id || r.updateId || null,
      outcome,
    };
  });
}

export async function getDeviceAuditLog(serialNumber, updateId, opts) {
  const headers = await authHeaders();
  // view labels the service's audit entry ("audit" when reached from a
  // Failed/Rollback row, TC-0031); informational only, the data is the same.
  const viewParam = opts && opts.view ? `&view=${encodeURIComponent(opts.view)}` : "";
  const url = `${MFA_AUTH_BASE}/device/logs/download?serial_number=${encodeURIComponent(serialNumber)}&update_id=${encodeURIComponent(updateId)}${viewParam}`;
  const res = await fetch(url, { headers });
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    if (res.status === 404) return [];
    throw new Error(`GET /device/logs/download \u2192 ${res.status}${body ? ": " + body : ""}`);
  }
  const data = await res.json().catch(() => ({}));
  let records;
  if (Array.isArray(data)) {
    records = data;
  } else if (data?.logs || data?.events || data?.items || data?.data) {
    records = data.logs ?? data.events ?? data.items ?? data.data;
  } else if (data && typeof data === "object" && Object.keys(data).length > 0) {
    records = [data];
  } else {
    records = [];
  }
  return records;
}

export async function getDeviceAppLog(serialNumber, updateId) {
  const headers = await authHeaders();
  const url = `${MFA_AUTH_BASE}/app/logs/download?serial_number=${encodeURIComponent(serialNumber)}&update_id=${encodeURIComponent(updateId)}`;
  const res = await fetch(url, { headers });
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    if (res.status === 404) return [];
    throw new Error(`GET /app/logs/download \u2192 ${res.status}${body ? ": " + body : ""}`);
  }
  const data = await res.json().catch(() => ({}));
  let records;
  if (Array.isArray(data)) {
    records = data;
  } else if (data?.logs || data?.events || data?.items || data?.data) {
    records = data.logs ?? data.events ?? data.items ?? data.data;
  } else if (data && typeof data === "object" && Object.keys(data).length > 0) {
    records = [data];
  } else {
    records = [];
  }
  return records;
}

// ── Activity Sessions: Clients ──────────────────────────────────────────────────
// list-clients only returns client-uid/name today (no role / log-count / device-count
// yet), so those fields default to "-"/0 until a real endpoint supplies them.
export async function getClients() {
  let realClients = [];
  try {
    const token = await getAccessToken();
    const response = await axios.get(`${MFA_AUTH_BASE}/Activity-sessions/list-clients`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    const data = response?.data ?? {};
    realClients = Array.isArray(data) ? data : (data?.clients ?? []);
  } catch (err) {
    handleUnauthorizedSession(err);
    console.error("[Clients] list-clients failed —", err?.message || err);
  }

  const byId = new Map();
  realClients.filter(Boolean).forEach((c) => {
    const id = c["client-uid"] || c.client_uid;
    if (!id || byId.has(id)) return;
    byId.set(id, {
      id,
      name: c.name || "",
      role: "-",
      logCount: 0,
      devicesTouched: 0,
      alarmsHandled: 0,
    });
  });
  return Array.from(byId.values());
}

// Client-log (Activity-sessions/client-log) returns client_uid/name/channel/count/sessions, with
// each session carrying session_id/received_time/serial_number/s3_bucket/s3_key — channel is
// client-level (same for every session), the rest is per-session.
export async function getClientActivityLogs(clientUid) {
  let realLogs = [];
  try {
    const token = await getAccessToken();
    const response = await axios.get(`${MFA_AUTH_BASE}/Activity-sessions/client-log`, {
      params: { client_uid: clientUid },
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    const data = response?.data ?? {};
    const sessions = Array.isArray(data) ? data : (data?.sessions ?? []);
    const channel = Array.isArray(data) ? "-" : (data?.channel || "-");

    realLogs = sessions.map((s) => ({
      sessionId: s.session_id || "",
      channel,
      device: s.serial_number || "-",
      date: s.received_time || "",
    }));
  } catch (err) {
    handleUnauthorizedSession(err);
    if (Number(err?.response?.status) !== 404) {
      console.error("[ClientActivityLogs]", err?.message || err);
    }
  }

  return realLogs;
}

export async function getClientLog(clientId, sessionId) {
  const listRows = await getClientActivityLogs(clientId);
  const row = listRows.find((r) => r.sessionId === sessionId);
  if (!row) return null;

  return buildClientLogDetail(row);
}

// ── Activity Sessions: Devices ───────────────────────────────────────────────────
// list-all-devices only returns serial numbers today (no model / log-count / client-count
// yet), so those fields default to "-"/0 until a real endpoint supplies them.
export async function getDeviceSessions() {
  let realSerials = [];
  try {
    const token = await getAccessToken();
    const response = await axios.get(`${MFA_AUTH_BASE}/Activity-sessions/list-all-devices`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    const data = response?.data ?? {};
    realSerials = Array.isArray(data) ? data : (data?.devices ?? []);
  } catch (err) {
    handleUnauthorizedSession(err);
    console.error("[DeviceSessions] list-all-devices failed —", err?.message || err);
  }

  const bySerial = new Map();
  realSerials.filter(Boolean).forEach((serial) => {
    if (!bySerial.has(serial)) {
      bySerial.set(serial, { serial, model: "-", status: null, logCount: 0, clientsTouched: 0 });
    }
  });
  return Array.from(bySerial.values());
}

// Device-Activity-log returns session_id/received_time/clientid/channel/ownerName —
// only session_id, channel, ownerName, and date (received_time) are shown in the UI.
export async function getDeviceActivityLogs(serial) {
  let realLogs = [];
  try {
    const token = await getAccessToken();
    const response = await axios.get(`${MFA_AUTH_BASE}/Activity-sessions/Device-Activity-log`, {
      params: { serial_number: serial },
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    const data = response?.data ?? {};
    const sessions = Array.isArray(data) ? data : (data?.sessions ?? []);

    realLogs = sessions.map((s) => ({
      sessionId: s.session_id || "",
      channel: s.channel || "-",
      ownerName: s.ownerName || "-",
      date: s.received_time || "",
    }));
  } catch (err) {
    handleUnauthorizedSession(err);
    if (Number(err?.response?.status) !== 404) {
      console.error("[DeviceActivityLogs]", err?.message || err);
    }
  }

  return realLogs;
}

// Builds session-detail content for a Device-Activity-log row from real fields
// only — patientId/verified/durationMin have no backing endpoint yet, so they're
// left null/false rather than fabricated. clientEvents/deviceEvents start empty;
// the Log View is filled separately from the real device-log-view/client-log-view APIs.
function buildLogDetail(serial, row) {
  const startedAt = row.date && !Number.isNaN(new Date(row.date).getTime())
    ? new Date(row.date).toISOString()
    : null;

  return {
    channel: row.channel || "-",
    clientId: row.ownerName || "-",
    portalSessionId: row.sessionId,
    clientSessionId: "",
    patientId: null,
    startedAt,
    durationMin: null,
    bundle: { clientEvents: 0, deviceEvents: 0 },
    verified: false,
    sessionSummary: "",
    clientEvents: [],
    deviceEvents: [],
  };
}

// Same shape as buildLogDetail, but for a Client-Activity-log row opened from
// the client side — "device" is the counterpart identifier instead of "clientId".
function buildClientLogDetail(row) {
  const startedAt = row.date && !Number.isNaN(new Date(row.date).getTime())
    ? new Date(row.date).toISOString()
    : null;

  return {
    channel: row.channel || "-",
    device: row.device || "-",
    portalSessionId: row.sessionId,
    clientSessionId: "",
    patientId: null,
    startedAt,
    durationMin: null,
    bundle: { clientEvents: 0, deviceEvents: 0 },
    verified: false,
    sessionSummary: "",
    clientEvents: [],
    deviceEvents: [],
  };
}

// Device-log-view returns the parsed device log file (ts/event/detail rows)
// for one session_id. Pagination (100 rows/page) is done client-side — the
// endpoint has no page params and always returns the full events list.
export async function getDeviceLogView(sessionId) {
  try {
    const token = await getAccessToken();
    const response = await axios.get(`${MFA_AUTH_BASE}/Activity-sessions/device-log-view`, {
      params: { session_id: sessionId },
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    const data = response?.data ?? {};
    const events = Array.isArray(data.events) ? data.events : [];
    return {
      sessionId: data.session_id || sessionId,
      serialNumber: data.serial_number || "",
      count: data.count ?? events.length,
      events,
    };
  } catch (err) {
    handleUnauthorizedSession(err);
    if (Number(err?.response?.status) !== 404) {
      console.error("[DeviceLogView]", err?.message || err);
    }
    return null;
  }
}

// Client-log-view returns the parsed client/app log file (ts/event/detail rows)
// for one session_id. Pagination (100 rows/page) is done client-side — the
// endpoint has no page params and always returns the full events list.
export async function getClientLogView(sessionId) {
  try {
    const token = await getAccessToken();
    const response = await axios.get(`${MFA_AUTH_BASE}/Activity-sessions/client-log-view`, {
      params: { session_id: sessionId },
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    const data = response?.data ?? {};
    const events = Array.isArray(data.events) ? data.events : [];
    return {
      sessionId: data.session_id || sessionId,
      serialNumber: data.serial_number || "",
      deviceId: data.deviceId || "",
      count: data.count ?? events.length,
      events,
    };
  } catch (err) {
    handleUnauthorizedSession(err);
    if (Number(err?.response?.status) !== 404) {
      console.error("[ClientLogView]", err?.message || err);
    }
    return null;
  }
}

// ── Activity Sessions: Analytics ─────────────────────────────────────────────
// Session volume and outcomes across the fleet, from the Athena table the merge
// lambda feeds. FleetAdmin only (role_matrix: ADMIN) — both calls 403 otherwise.
//
// Outcomes are `completed`, `incomplete` and `unknown`. `unknown` usually means
// the phone never uploaded its log; it is NOT a failed therapy and must stay its
// own series on the chart. `deviceAbortFlagged` overlaps the others and is
// informational: the pump log commonly ends with "Therapy aborted" even when the
// phone reports a completed therapy.

function mapAnalytics(data) {
  const totals = data?.totals ?? {};
  const coverage = data?.coverage ?? {};
  return {
    generatedAt: data?.generated_at ?? null,
    source: data?.source ?? null,
    stale: Boolean(data?.stale),
    noData: Boolean(data?.no_data),
    from: data?.from ?? "",
    to: data?.to ?? "",
    totals: {
      total: totals.total ?? 0,
      completed: totals.completed ?? 0,
      incomplete: totals.incomplete ?? 0,
      unknown: totals.unknown ?? 0,
      deviceAbortFlagged: totals.device_abort_flagged ?? 0,
      devices: totals.devices ?? 0,
    },
    series: (Array.isArray(data?.series) ? data.series : []).map((d) => ({
      date: d.date ?? "",
      total: d.total ?? 0,
      completed: d.completed ?? 0,
      incomplete: d.incomplete ?? 0,
      unknown: d.unknown ?? 0,
      deviceAbortFlagged: d.device_abort_flagged ?? 0,
      devices: d.devices ?? 0,
    })),
    coverage: {
      unknownShare: coverage.unknown_share ?? 0,
      daysWithData: coverage.days_with_data ?? 0,
    },
    notes: Array.isArray(data?.notes) ? data.notes : [],
    auditRecorded: data?.audit_recorded !== false,
  };
}

/**
 * The cached summary. Returns null on failure.
 *
 * DELIBERATELY NOT the swallow-and-return-[] convention the Activity-Sessions
 * getters above use: a chart silently showing zero sessions is worse than an
 * error message, because zero is a plausible number. The page renders an error
 * state on null.
 */
export async function getSessionAnalytics({ from, to } = {}) {
  try {
    const token = await getAccessToken();
    const params = {};
    if (from) params.from = from;
    if (to) params.to = to;
    const response = await axios.get(`${MFA_AUTH_BASE}/analytics/sessions/summary`, {
      params,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return mapAnalytics(response?.data ?? {});
  } catch (err) {
    handleUnauthorizedSession(err);
    console.error("[SessionAnalytics]", err?.message || err);
    return null;
  }
}

/**
 * Run a live (billed) Athena query and return the fresh summary.
 *
 * THROWS rather than swallowing, unlike its neighbours: a refresh is something
 * the user asked for, so a rate limit or a failure has to reach them. The thrown
 * error carries `status`, `reason` and `retryAfterSeconds`.
 *
 * A 202 means the query is still running — the resolved value carries
 * `{ running: true, queryExecutionId }` and the caller polls by passing that id
 * back in.
 */
export async function refreshSessionAnalytics({ from, to, queryExecutionId } = {}) {
  try {
    const token = await getAccessToken();
    const body = {};
    if (from) body.from = from;
    if (to) body.to = to;
    if (queryExecutionId) body.query_execution_id = queryExecutionId;
    const response = await axios.post(
      `${MFA_AUTH_BASE}/analytics/sessions/refresh`,
      body,
      { headers: token ? { Authorization: `Bearer ${token}` } : {} },
    );
    if (response?.status === 202) {
      return {
        running: true,
        queryExecutionId: response?.data?.query_execution_id ?? null,
        pollAfterMs: response?.data?.poll_after_ms ?? 2000,
      };
    }
    return { running: false, ...mapAnalytics(response?.data ?? {}) };
  } catch (err) {
    handleUnauthorizedSession(err);
    const status = Number(err?.response?.status) || 0;
    const data = err?.response?.data ?? {};
    const error = new Error(data.message || `Refresh failed (${status || "network"})`);
    error.status = status;
    error.reason = data.reason ?? null;
    error.retryAfterSeconds = data.retry_after_seconds ?? null;
    throw error;
  }
}

export async function getDeviceLog(serial, sessionId) {
  const listRows = await getDeviceActivityLogs(serial);
  const row = listRows.find((r) => r.sessionId === sessionId);
  if (!row) return null;

  return buildLogDetail(serial, row);
}

export async function getFleetOverview({ page = 1, limit = 10, country = "", fromDate = "", toDate = "", search = "", status = "", view = "", hubTile = "", hubFilter = "" } = {}) {
  const headers = await authHeaders();
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
  });
  if (country) params.set("country", country);
  if (fromDate) params.set("from-date", fromDate);
  if (toDate) params.set("to-date", toDate);
  // Which screen asked, recorded in the service's audit entry (TC-0007 FT-004).
  // Informational only: the service decides nothing from it.
  if (view) params.set("view", view);
  // Which hub tile deep-linked here and the filter it landed on (TC-0004 FT-004),
  // sent with view=hub_tile. Labels only - the service records them as such.
  if (hubTile) params.set("hub_tile", hubTile);
  if (hubFilter) params.set("hub_filter", hubFilter);
  // Applied by the service before pagination: serial / model / user / country /
  // firmware text, and up_to_date | update_required | unknown.
  if (search) params.set("search", search);
  if (status) params.set("status", status);

  const res = await fetch(`${MFA_AUTH_BASE}/fleetoverview/listdevices?${params.toString()}`, { headers });
  if (!res.ok) throw new Error(`GET /fleetoverview/listdevices → ${res.status}`);

  const data = await res.json().catch(() => ({}));
  const records = Array.isArray(data)
    ? data
    : (data?.devices ?? data?.fleets ?? data?.items ?? data?.data ?? []);

  const devices = records.map((d, index) => {
    const currentFw = d.currentFw || d.current_fw || d.current_fw_version || d.currentVersion || d.current_version || "-";
    const requiredFw = d.requiredFw ?? d.required_fw ?? d.required_fw_version ?? d.targetVersion ?? d.target_version ?? null;
    const rawApiStatus = d.status ?? d.updateStatus ?? d.update_status ?? d.deviceStatus ?? d.device_status ??
      d.fwStatus ?? d.fw_status ?? d.updateState ?? d.update_state;
    const apiStatus = rawApiStatus == null ? "" : String(rawApiStatus);
    const status = normaliseDeviceStatus(rawApiStatus);

    const serial = d.serial || d.serial_number || d.serialNo || d.serial_no || d.deviceSerial || d.device_serial || d.id || `device-${index + 1}`;

    return {
      id: d.id || d.deviceId || d.device_id || serial,
      serial,
      model: d.model || d.deviceModel || d.device_model || d.type || "Unknown",
      currentCountry: d.current_country || d.currentCountry || null,
      baseCountry: d.base_country || d.baseCountry || null,
      registeredAt: d.registered_at || d.registeredAt || null,
      currentFw,
      requiredFw,
      status,
      apiStatus: apiStatus || null,
      // No fallback: never-synced stays empty rather than showing "now" (DISC-001).
      lastUpdated: d.lastUpdated || d.last_updated || d.updatedAt || d.updated_at || null,
      agingDays: d.agingDays ?? d.aging_days ?? d.age_days ?? null,
      segment: d.segment || d.segmentId || d.segment_id || null,
      segmentName: d.segmentName || d.segment_name || d.fleetName || d.fleet_name || null,
      name: d.name || d.username || d.userName || d.user_name || null,
      // FMP-AN-009: on a stale record `status` is the last known one, not current.
      stale: d.stale === true,
      lastReportedAt: d.last_reported_at ?? null,
      dataAgeDays: d.data_age_days ?? null,
    };
  });

  // A genuinely absent field must stay distinguishable from a real 0 all the
  // way up to the caller - defaulting to 0 here would make a missing/omitted
  // backend field indistinguishable from an honestly-reported empty fleet.
  const numOrNull = (v) => (v === undefined || v === null ? null : Number(v));

  const rawSummary = data?.status_summary;
  // outcome_summary splits the devices that are not Up to Date into pending and
  // failed over the WHOLE filtered fleet, so the Hub no longer shows those two
  // counts as unavailable (TP-FMP-05-DISC-015). status_summary carries no such keys.
  const rawOutcomes = data?.outcome_summary && typeof data.outcome_summary === "object" ? data.outcome_summary : {};
  const statusSummary = rawSummary && typeof rawSummary === "object"
    ? {
        updated: numOrNull(rawSummary.updated ?? rawSummary.up_to_date ?? rawSummary["Up to Date"]),
        pending: numOrNull(rawSummary.pending ?? rawSummary.Pending ?? rawOutcomes.pending),
        mandatory: numOrNull(rawSummary.mandatory ?? rawSummary["Update Required"] ?? rawSummary.update_required),
        failed: numOrNull(rawSummary.failed ?? rawSummary.failed_rollback ?? rawSummary.Failed ?? rawOutcomes.failed),
        unknown: numOrNull(rawSummary.unknown ?? rawSummary["Unknown"]),
      }
    : null;
  const totalDevices = Number(data?.total_devices ?? devices.length ?? 0);
  const pag = data?.pagination || {};

  return {
    totalDevices,
    pagination: {
      page: Number(pag.page ?? page),
      totalPages: Number(pag.total_pages ?? 1),
      total: Number(pag.total_records ?? totalDevices),
    },
    statusSummary,
    devices,
  };
}

export async function getDevice(serial) {
  if (USE_MOCK || USE_MOCK_DEVICES) return MOCK_DEVICES.find((d) => d.serial === serial) || null;
  return apiGet(`/devices/${serial}`);
}

// ── Telemetry ──────────────────────────────────────────────────────────────────
export async function getTelemetry(filters = {}) {
  if (USE_MOCK || USE_MOCK_TELEMETRY) {
    let records = [...MOCK_TELEMETRY];
    if (filters.serial) records = records.filter((t) => t.deviceSerial === filters.serial);
    if (filters.rolloutId) records = records.filter((t) => t.rolloutId === filters.rolloutId);
    return records;
  }
  const params = new URLSearchParams(filters).toString();
  return apiGet(`/telemetry?${params}`);
}

// Returns { devices, total }. `total` is the population the service reports for
// the requested countries - it is the denominator the rollout tier cap is a
// percentage of, so it must come from the service rather than from the length of
// whatever subset happened to be returned.
function normaliseCountryWiseDevices(data) {
  const rawDevices = Array.isArray(data)
    ? data
    : (data?.devices ?? data?.items ?? data?.records ?? data?.data ?? []);

  const seen = new Set();
  const devices = rawDevices
    .map((d, index) => {
      if (typeof d === "string") {
        const id = d.trim();
        return id ? { id, name: id, country: null, serial_no: id } : null;
      }

      if (!d || typeof d !== "object") return null;

      const id = (
        d.device_id || d.deviceId || d.id || d.serial_number || d.serialNumber || d.serial || d.name || `device-${index + 1}`
      ).toString();
      const serialNo = (
        d.serial_number || d.serialNumber || d.serial_no || d.serialNo || d.serial || d.device_serial || d.deviceSerial || d.device_no || d.deviceNo || id
      ).toString();
      const name = (
        d.device_name || d.deviceName || d.name || d.serial_number || d.serial || d.device_id || d.deviceId || id
      ).toString();
      const country = (d.country || d.country_name || d.countryName || "").toString() || null;

      return {
        id,
        name,
        country,
        serial_no: serialNo,
      };
    })
    .filter(Boolean)
    .filter((d) => {
      const key = `${d.id}::${d.name}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

  const reportedTotal = Number(data?.count);
  return {
    devices,
    total: Number.isFinite(reportedTotal) ? reportedTotal : devices.length,
  };
}

export async function getCountryWiseDeviceList(countries = []) {
  const countryList = Array.isArray(countries)
    ? countries.map((c) => (c || "").toString().trim()).filter(Boolean)
    : [];

  if (countryList.length === 0) return { devices: [], total: 0 };

  try {
    const token = await getAccessToken();
    const params = new URLSearchParams();
    countryList.forEach((country) => params.append("country", country));

    const response = await axios.get(`${MFA_AUTH_BASE}/country-wise-device-list`, {
      params,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });

    return normaliseCountryWiseDevices(response?.data);
  } catch (err) {
    const message = err?.response?.data?.message || err?.message || "Failed to fetch country-wise devices.";
    throw new Error(message);
  }
}

// exportAll: ask for the COMPLETE filtered set in one response instead of a page.
// The service's ?export=json does exactly this, and `limit` must then be OMITTED -
// it truncates an export when supplied ("if (... or export_mode) and limit_supplied:
// records = records[:limit]"), so sending limit=10 would hand back ten rows and
// call it the whole fleet.
export async function getDeviceCompliance({ page = 1, limit = 10, search = "", filter = "ALL", country = "", exportAll = false } = {}) {
  try {
    const token = await getAccessToken();
    const filterValue = (filter || "").toString().trim().toUpperCase();
    const complianceFilterValue = filterValue === "COMPLIANT"
      ? "Compliant"
      : filterValue === "NON_COMPLIANT"
        ? "Non-Compliant"
        : "";
    const countryValue = (country || "").toString().trim();

    const params = {
      ...(exportAll
        ? { export: "json" }
        : { page: String(page), page_size: String(limit), limit: String(limit) }),
      // `search`, not `serial_number`. serial_number is an exact key lookup -
      // the service does get_item on it and answers 404 for anything that is
      // not a whole serial - so a partial term like "xx" came back as
      // "Device 'xx' not found". `search` is the substring match across
      // serial_number, model and country, which is what the box means.
      ...(search ? { search } : {}),
      ...(countryValue ? { country: countryValue.toLowerCase() } : {}),
      ...(complianceFilterValue ? { compliance_filter: complianceFilterValue } : {}),
    };

    const response = await axios.get(`${MFA_AUTH_BASE}/Reports/device-compliance`, {
      params,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });

    const data = response?.data ?? {};
    const rawRecords = Array.isArray(data)
      ? data
      : (data?.devices ?? data?.items ?? data?.records ?? data?.data ?? []);

    const devices = rawRecords.map((d, index) => {
      const serial = d.serial || d.serial_number || d.serialNo || d.serial_no || d.deviceSerial || d.device_serial || d.id || `device-${index + 1}`;
      const model = d.model || d.device_model || d.deviceModel || d.type || "Unknown";
      const country = d.country || d.country_name || d.countryName || d.region || "—";
      const currentFw = d.current_fw || d.currentFw || d.current_fw_version || d.currentVersion || d.current_version || "—";
      const requiredFw = d.required_fw || d.requiredFw || d.required_fw_version || d.targetVersion || d.target_version || data?.required_fw || "—";
      const lastUpdated = d.ota_success_updated_at || d.last_ota || d.lastUpdated || d.last_updated || d.updatedAt || d.updated_at || null;

      const rawStatus = (
        d.compliance || d.compliance_status || d.complianceStatus || d.status || d.device_status || ""
      ).toString().trim().toUpperCase();

      let status = "NON_COMPLIANT";
      if (/NON[\s_-]?COMPLIANT|NOT[\s_-]?COMPLIANT/.test(rawStatus)) {
        status = "NON_COMPLIANT";
      } else if (rawStatus.includes("PENDING")) {
        status = "PENDING";
      } else if (rawStatus.includes("BLOCKED")) {
        // Below the minimum firmware its rollout requires, so it cannot install
        // the build yet (SVC-MD-003). Not a fault of the device, and not the
        // same thing as Unknown, which means no rollout targets it at all.
        status = "BLOCKED";
      } else if (rawStatus.includes("UNKNOWN")) {
        // The service says Unknown when no mandatory rollout cohort contains
        // this device: there is no required build, so it cannot be behind one.
        // UNKNOWN matched none of the branches and fell through to the
        // NON_COMPLIANT default, reporting a fault that had not been found.
        status = "UNKNOWN";
      } else if (rawStatus.includes("COMPLIANT") || rawStatus.includes("UP_TO_DATE") || rawStatus.includes("CURRENT")) {
        status = "UP_TO_DATE";
      }

      const deltaRaw = d.delta_versions ?? d.deltaVersions ?? d.delta_version ?? d.version_delta ?? d.behind_count ?? d.versions_behind;
      let parsedDelta = null;
      if (deltaRaw !== null && deltaRaw !== undefined && deltaRaw !== "") {
        const parsed = Number(deltaRaw);
        if (Number.isFinite(parsed)) parsedDelta = parsed;
      }
      const deltaText = (d.delta_text ?? d.delta ?? "").toString().trim();
      const deltaCount = parsedDelta !== null
        ? Math.max(parsedDelta, 0)
        : (currentFw && requiredFw && currentFw === requiredFw ? 0 : null);

      let deltaType = "UNKNOWN";
      let deltaLabel = "";
      if (deltaCount === 0) {
        deltaType = "CURRENT";
      } else if (deltaCount && deltaCount > 0) {
        deltaType = "BEHIND";
        deltaLabel = `${deltaCount} version${deltaCount > 1 ? "s" : ""} behind`;
      } else if (/^current$/i.test(deltaText)) {
        deltaType = "CURRENT";
      } else if (deltaText) {
        deltaType = "BEHIND";
        deltaLabel = deltaText;
      }

      if (!rawStatus && deltaType === "CURRENT") {
        status = "UP_TO_DATE";
      }

      return {
        id: d.id || d.device_id || serial,
        serial,
        model,
        country,
        currentFw,
        requiredFw,
        status,
        lastUpdated,
        deltaType,
        deltaCount: deltaCount ?? 0,
        deltaLabel,
        // FMP-AN-009: `status` is the last known compliance; on a stale record it is
        // not current. Only an explicit true flags it, so an older service that
        // sends no flag renders exactly as before.
        stale: d.stale === true,
        lastReportedAt: d.last_reported_at ?? null,
        dataAgeDays: d.data_age_days ?? null,
      };
    });

    const pagination = data?.pagination ?? {};
    const limitValue = Number(pagination.limit ?? data?.limit ?? limit);
    const totalRecords = Number(
      pagination.total_records ?? pagination.totalRecords ?? data?.total_count ?? data?.totalRecords ?? devices.length,
    );
    const totalPages = Number(
      pagination.total_pages ?? pagination.totalPages ?? data?.total_pages ?? Math.max(1, Math.ceil(totalRecords / limit)),
    );
    const currentPage = Number(pagination.page ?? data?.page ?? page);

    const complianceSummary = data?.compliance_summary ?? data?.summary ?? {};
    const compliantDevices = Number(
      data?.compliant_devices ?? data?.compliant_count ?? complianceSummary?.compliant ?? devices.filter((x) => x.status === "UP_TO_DATE").length,
    );
    const totalDevices = Number(data?.total_devices ?? data?.total_count ?? data?.count ?? data?.summary?.total ?? totalRecords);
    const compliantPct = totalDevices > 0 ? ((compliantDevices / totalDevices) * 100).toFixed(1) : "0.0";
    const staleDevices = Number(complianceSummary?.stale ?? 0) || 0;
    const freshnessThresholdDays = data?.freshness_threshold_days ?? null;

    return {
      devices,
      summary: {
        totalDevices,
        compliantDevices,
        compliantPct,
        staleDevices,
        freshnessThresholdDays,
      },
      pagination: {
        page: currentPage,
        limit: limitValue,
        totalRecords,
        totalPages,
      },
    };
  } catch (err) {
    handleUnauthorizedSession(err);
    console.error("[DeviceCompliance]", err);
    const message = err?.response?.data?.message || err?.message || "Failed to fetch device compliance data.";
    throw new Error(message);
  }
}

// The server builds the whole file in one request. `filters` are optional and
// default to none, so a caller that wants the entire fleet - the Export page -
// keeps calling this with just a format and is unaffected.
export async function downloadDeviceComplianceReport(format = "csv", filters = {}) {
  try {
    const token = await getAccessToken();
    const requested = String(format || "csv").toLowerCase();
    const normalisedFormat = ["pdf", "json"].includes(requested) ? requested : "csv";

    const { search = "", filter = "ALL", country = "", fromDate = "", toDate = "" } = filters;
    const filterValue = (filter || "").toString().trim().toUpperCase();
    const complianceFilterValue = filterValue === "COMPLIANT"
      ? "Compliant"
      : filterValue === "NON_COMPLIANT"
        ? "Non-Compliant"
        : "";
    const searchValue = (search || "").toString().trim();
    const countryValue = (country || "").toString().trim();

    const response = await axios.get(`${MFA_AUTH_BASE}/Reports/device-compliance`, {
      params: {
        format: normalisedFormat,
        // JSON as an export is the COMPLETE filtered set, not one page of the view.
        ...(normalisedFormat === "json" ? { export: "json" } : {}),
        // Date range on each device's latest report (FMP-RP-003).
        ...(fromDate ? { "from-date": fromDate } : {}),
        ...(toDate ? { "to-date": toDate } : {}),
        // `search` is a substring match across serial, model and country, the
        // same match the table's search box applies. `serial_number` is NOT
        // used here: it is an exact key lookup, so a partial term would return
        // a 404 rather than the rows the operator can see.
        ...(searchValue ? { search: searchValue } : {}),
        ...(countryValue ? { country: countryValue.toLowerCase() } : {}),
        ...(complianceFilterValue ? { compliance_filter: complianceFilterValue } : {}),
      },
      responseType: "blob",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });

    const contentDisposition = response?.headers?.["content-disposition"] || "";
    const filenameMatch = contentDisposition.match(/filename\*?=(?:UTF-8''|")?([^";]+)/i);
    const filenameFromHeader = filenameMatch ? decodeURIComponent(filenameMatch[1].replace(/"/g, "")) : "";
    const fallbackName = `device-compliance-report.${normalisedFormat}`;

    return {
      blob: response?.data,
      filename: filenameFromHeader || fallbackName,
      contentType: response?.headers?.["content-type"] || "application/octet-stream",
    };
  } catch (err) {
    handleUnauthorizedSession(err);
    const message = err?.response?.data?.message || err?.message || "Failed to download device compliance report.";
    throw new Error(message);
  }
}

export async function exportFullAuditLogReport({ serialNumber, fromDate, toDate }) {
  try {
    const token = await getAccessToken();
    const response = await axios.get(`${MFA_AUTH_BASE}/Export-Report/FullAuditlog`, {
      params: {
        serial_number: serialNumber,
        from_date: fromDate,
        to_date: toDate,
      },
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });

    return response?.data ?? {};
  } catch (err) {
    handleUnauthorizedSession(err);
    const message = err?.response?.data?.message || err?.message || "Failed to export full audit log report.";
    throw new Error(message);
  }
}

// ── Deprecated Logs ────────────────────────────────────────────────────────────
export async function getDeprecatedLogs({ page = 1, limit = 10 } = {}) {
  try {
    const token = await getAccessToken();
    const params = {
      page: String(page),
      page_size: String(limit),
      pageSize: String(limit),
      limit: String(limit),
      category: "deprecate",
    };
    const response = await axios.get(`${MFA_AUTH_BASE}/device/fulllogs`, {
      params,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    const data = response?.data ?? {};
    const rawList = Array.isArray(data)
      ? data
      : (data?.activities ?? data?.items ?? data?.records ?? data?.logs ?? data?.data ?? []);
    const devices = rawList.map((r) => ({
      id: r.trace_id || r.id || r.auditId || r.audit_id || r.logId || String(Math.random()),
      serial: r.details?.serial_number || r.serial_number || r.serial || r.deviceSerial || "—",
      model: r.device_model || r.model || "—",
      currentFw: r.details?.version || r.current_fw || r.fwVersion || r.fw_version || r.version || r.firmwareVersion || "—",
      requiredFw: r.target_fw || r.requiredFw || r.required_fw || "—",
      status: "DEPRECATED",
      lastUpdated: r.timestamp || r.ts || r.uploaded_at || r.createdAt || r.created_at || new Date().toISOString(),
      deltaType: "UNKNOWN",
      deltaCount: 0,
      deltaLabel: "Deprecated",
    }));

    const pagination = {
      page: data?.page ?? page,
      limit: data?.page_size ?? limit,
      totalRecords: data?.total_count ?? data?.totalRecords ?? devices.length,
      totalPages: data?.total_pages ?? data?.totalPages ?? 1,
    };

    return {
      devices,
      summary: {
        totalDevices: devices.length,
        compliantDevices: 0,
        compliantPct: "0.0",
      },
      pagination,
    };
  } catch (err) {
    handleUnauthorizedSession(err);
    console.error("[DeprecatedLogs]", err);
    const message = err?.response?.data?.message || err?.message || "Failed to fetch deprecated logs.";
    throw new Error(message);
  }
}

// ── Device Catalog ───────────────────────────────────────────────────────────
export async function getFieldDeviceSerials() {
  try {
    const token = await getAccessToken();
    const response = await axios.get(`${MFA_AUTH_BASE}/factory/device/list`, {
      params: { registration_filter: "field" },
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    const data = response?.data ?? {};
    const rawList = Array.isArray(data)
      ? data
      : (data?.devices ?? data?.items ?? data?.data ?? data?.records ?? []);
    return rawList
      .map((d) => d.serial_number || d.serialNumber || d.serial || d.device_id || d.id || "")
      .filter(Boolean);
  } catch (err) {
    handleUnauthorizedSession(err);
    const message = err?.response?.data?.message || err?.message || "Failed to fetch field device serials.";
    throw new Error(message);
  }
}

export async function getDeviceCatalog({ registrationFilter = "all", page = 1, limit = 10, status, country } = {}) {
  try {
    const headers = await authHeaders();
    const params = new URLSearchParams({
      registration_filter: registrationFilter,
      page: String(page),
      page_size: String(limit),
      limit: String(limit),
    });
    if (status) params.set("status", status);
    if (country) params.set("country", country);
    const res = await fetch(`${MFA_AUTH_BASE}/factory/device/list?${params.toString()}`, { headers });
    if (!res.ok) throw new Error(`GET /factory/device/list → ${res.status}`);
    const data = await res.json().catch(() => ({}));
    const rawList = Array.isArray(data)
      ? data
      : (data?.devices ?? data?.items ?? data?.data ?? data?.records ?? []);
    const devices = rawList.map((d) => ({
      deviceId: d.device_id || d.deviceId || d.id || "—",
      serialNumber: d.serial_number || d.serialNumber || d.serial || "—",
      macId: d.mac_id || d.macId || d.mac_address || "—",
      country: d.country || d.region || d.location || "—",
      registration: d.registration || d.registration_filter || registrationFilter,
      status: d.status || d.device_status || "—",
      fieldInUse: d.field_in_use || d.fieldInUse || d.filed_in_use || "no",
      lastSeen: d.last_seen || d.lastSeen || d.updated_at || "—",
      factoryAllocatedTime: d.factory_allocated_time || d.factoryAllocatedTime || null,
      firstFieldInUse: d.first_field_in_use || d.firstFieldInUse || null,
      latestFieldInUse: d.latest_field_in_use || d.latestFieldInUse || null,
      username: d.username || d.name || d.userName || "—",
    }));

    // Support both nested pagination payloads and top-level pagination fields.
    const pag = data?.pagination || {};
    const currentPage = Number(pag.page ?? data?.page ?? page) || page;
    const limitValue = Number(pag.limit ?? pag.page_size ?? data?.limit ?? data?.page_size ?? limit) || limit;
    const totalRecords = Number(
      pag.total_count
      ?? pag.total_records
      ?? pag.totalRecords
      ?? data?.total_count
      ?? data?.total_records
      ?? data?.totalRecords
      ?? data?.count
      ?? devices.length,
    ) || devices.length;
    const totalPages = Number(
      pag.total_pages
      ?? pag.totalPages
      ?? data?.total_pages
      ?? data?.totalPages
      ?? Math.max(1, Math.ceil(totalRecords / limitValue)),
    ) || 1;

    return {
      devices,
      pagination: {
        page: currentPage,
        totalPages,
        totalRecords,
      },
      latestUpdatedAt: data?.latest_updated_at || data?.latestUpdatedAt || null,
    };
  } catch (err) {
    console.error("[DeviceCatalog]", err);
    return { devices: [], pagination: { page: 1, totalPages: 1, totalRecords: 0 }, latestUpdatedAt: null };
  }
}

// ── Audit ──────────────────────────────────────────────────────────────────────
/**
 * Unusual activity — refused requests only (denied approvals, identity
 * mismatches, rejected tokens, failed sign-ins). Separate from getAuditLog,
 * which reads routine activity from Activity-Trace.
 */
export async function getSecurityEvents({ page = 1, limit = 10, fromDate, toDate, severity, search } = {}) {
  const token = await getAuthToken();
  const params = {
    page: String(page),
    limit: String(limit),
    ...(fromDate ? { "from-date": fromDate } : {}),
    ...(toDate ? { "to-date": toDate } : {}),
    ...(severity ? { severity } : {}),
    ...(search ? { search } : {}),
  };
  const response = await axios.get(`${MFA_AUTH_BASE}/security-events`, {
    params,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  const data = response?.data ?? {};
  return {
    records: Array.isArray(data.records) ? data.records : [],
    pagination: data.pagination ?? { page, limit, totalRecords: 0, totalPages: 1 },
  };
}

export async function getAuditLog({ page = 1, limit = 10, fromDate, toDate, category } = {}) {
  try {
    const token = await getAuthToken();
    const params = {
      page: String(page),
      page_size: String(limit),
      pageSize: String(limit),
      limit: String(limit),
      ...(fromDate ? { "from-date": fromDate } : {}),
      ...(toDate ? { "to-date": toDate } : {}),
      ...(category ? { category } : {}),
    };
    const response = await axios.get(`${MFA_AUTH_BASE}/device/fulllogs`, {
      params,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    const data = response?.data ?? {};
    const rawList = Array.isArray(data)
      ? data
      : (data?.activities ?? data?.items ?? data?.records ?? data?.logs ?? data?.data ?? []);
    const records = rawList.map((r) => {
      const eventType = (r.event_type || r.eventType || r.action || r.type || "—").toString();
      const eventTypeUpper = eventType.toUpperCase();
      let actorVal = "—";
      
      if (eventTypeUpper.includes("UPLOAD")) {
        actorVal = r.uploader_name || r.details?.uploader_name || r.uploaderName || r.actor || r.actorName || r.user || r.username || "—";
      } else if (eventTypeUpper.includes("PUBLISH")) {
        actorVal = r.publisher_name || r.details?.publisher_name || r.publisherName || r.actor || r.actorName || r.user || r.username || "—";
      } else {
        actorVal = r.actor || r.actorName || r.user || r.username || "—";
      }

      return {
        id:        r.trace_id  || r.id           || r.auditId       || r.audit_id || r.logId || String(Math.random()),
        serial:    r.details?.serial_number || r.serial_number || r.serial || r.deviceSerial || "—",
        actor:     actorVal,
        role:      r.actor_role || r.role        || r.actorRole      || r.userRole || "—",
        eventType: eventType,
        device:    r.details?.device_id || r.details?.file_name || r.device_baseline || r.device || r.deviceSerial || r.serial || r.deviceId || "—",
        fwVersion: r.details?.version || r.fwVersion || r.fw_version || r.version || r.firmwareVersion || "—",
        // No fallback to the time of viewing: an entry without a timestamp shows "-".
        ts:        r.timestamp  || r.ts          || r.uploaded_at    || r.createdAt || r.created_at || null,
      };
    });
    const pagination = {
      page: data?.page ?? page,
      limit: data?.page_size ?? limit,
      totalRecords: data?.total_count ?? data?.totalRecords ?? records.length,
      totalPages: data?.total_pages ?? data?.totalPages ?? 1,
    };
    return { records, pagination };
  } catch (err) {
    // Surfaced, not swallowed: an empty list would read as "no records" (FMP-AT-001).
    handleUnauthorizedSession(err);
    const status = err?.response?.status;
    const message = err?.response?.data?.message || err?.message || "Failed to load the activity trace.";
    throw new Error(status ? `${message} (HTTP ${status})` : message);
  }
}

// FMP-AT-001 export: the whole filtered audit log (every page) as CSV, from the
// same service and the same role gate as the list; the service records the export.
// FMP-RP-003 full-audit report from the WP-RPT-05b Export screen, served by the same
// audited Activity Trace endpoint as the Audit Log page (Fleet Admin / Release Manager,
// FMP-AT-001). CSV is the service's own file; JSON / PDF use ?export=, which returns the
// whole filtered set and is audited as AUDIT_LOG_EXPORTED (TP-FMP-05-DISC-001).
// Returns { blob, filename } for CSV and JSON, { activities } for PDF.
export async function exportActivityTraceReport({ format = "csv", fromDate, toDate, serialNumber } = {}) {
  const token = await getAuthToken();
  const fmt = String(format || "csv").toLowerCase();
  const params = new URLSearchParams({
    ...(fmt === "csv" ? { format: "csv" } : { export: fmt === "pdf" ? "pdf" : "json" }),
    ...(fromDate ? { "from-date": fromDate } : {}),
    ...(toDate ? { "to-date": toDate } : {}),
    ...(serialNumber ? { serial_number: serialNumber } : {}),
  });
  const res = await fetch(`${MFA_AUTH_BASE}/device/fulllogs?${params}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    let message = "";
    try { message = (await res.json())?.message || ""; } catch { /* not JSON */ }
    throw new Error(`Export failed: ${message || res.statusText || "error"} (HTTP ${res.status})`);
  }
  const stamp = new Date().toISOString().slice(0, 19).replace(/[-:]/g, "");
  if (fmt === "csv") {
    const text = await res.text();
    const disposition = res.headers.get("Content-Disposition") || "";
    const filename = /filename="([^"]+)"/.exec(disposition)?.[1] || `activity-trace_${stamp}Z.csv`;
    return { blob: new Blob([text], { type: "text/csv;charset=utf-8" }), filename };
  }
  const data = await res.json();
  if (fmt === "pdf") return { activities: data?.activities || [] };
  return {
    blob: new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }),
    filename: `activity-trace_${stamp}Z.json`,
  };
}

export async function exportAuditLog({ fromDate, toDate, category } = {}) {
  const token = await getAuthToken();
  const params = new URLSearchParams({
    format: "csv",
    ...(fromDate ? { "from-date": fromDate } : {}),
    ...(toDate ? { "to-date": toDate } : {}),
    ...(category ? { category } : {}),
  });
  const res = await fetch(`${MFA_AUTH_BASE}/device/fulllogs?${params}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    let message = "";
    try { message = (await res.json())?.message || ""; } catch { /* not JSON */ }
    throw new Error(`Export failed: ${message || res.statusText || "error"} (HTTP ${res.status})`);
  }
  const text = await res.text();
  const disposition = res.headers.get("Content-Disposition") || "";
  const fileName = /filename="([^"]+)"/.exec(disposition)?.[1]
    || `activity-trace_${new Date().toISOString().slice(0, 19).replace(/[-:]/g, "")}Z.csv`;
  const url = URL.createObjectURL(new Blob([text], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  // Data rows = lines after the header (the service writes CRLF; quoted cells may hold newlines,
  // so this is for the on-screen note only - the file is the record).
  return { fileName, rows: Math.max(0, text.split("\r\n").filter(Boolean).length - 1) };
}

// ── Users ──────────────────────────────────────────────────────────────────────
// `status: "pending"` returns only accounts not yet activated (invited or awaiting
// approval) - the unacknowledged-account report (TP-SAF-WEB-TS-08-TC-0003).
export async function getUsers({ page = 1, limit = 10, status } = {}) {
  if (USE_MOCK || USE_MOCK_USERS) {
    const start = (page - 1) * limit;
    const users = MOCK_USERS.slice(start, start + limit);
    return {
      users,
      pagination: {
        page,
        limit,
        total_records: MOCK_USERS.length,
        total_pages: Math.max(1, Math.ceil(MOCK_USERS.length / limit)),
      },
    };
  }
  const headers = await authHeaders();
  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (status) params.set("status", status);
  const res = await fetch(`${MFA_AUTH_BASE}/userslist?${params}`, { headers });
  if (!res.ok) throw new Error(`GET /userslist → ${res.status}`);
  const data = await res.json();
  // Normalise: API may return { users:[…] }, { Users:[…] }, { data:[…] } or a plain array.
  const users = Array.isArray(data)
    ? data
    : (data?.users ?? data?.Users ?? data?.data ?? []);
  const pagination = data?.pagination ?? {};
  return {
    users,
    pagination: {
      page: pagination.page ?? page,
      limit: pagination.limit ?? limit,
      total_records: pagination.total_records ?? pagination.totalRecords ?? users.length,
      total_pages: pagination.total_pages ?? pagination.totalPages ?? 1,
    },
  };
}

export async function inviteUser(payload) {
  if (USE_MOCK && !API_BASE_URL) {
    return { ...payload, sub: "sub-new-" + Date.now(), status: "FORCE_CHANGE_PASSWORD" };
  }

  const headers = await authHeaders({ "Content-Type": "application/json" });
  const res = await fetch(`${API_BASE_URL}/users/create`, {
    method: "POST",
    headers,
    body: JSON.stringify({
      email: payload.email,
      first_name: payload.first_name,
      middle_name: payload.middle_name || undefined,
      last_name: payload.last_name,
      phone_number: payload.phone,
      role: payload.role,
    }),
  });
  if (!res.ok) {
    const errText = await res.text().catch(() => "");
    throw new Error(`POST /users/create \u2192 ${res.status}${errText ? ": " + errText : ""}`);
  }
  const text = await res.text();
  return text ? JSON.parse(text) : {};
}

// Activates an account the invitee has acknowledged: creates the login and applies
// the role. FleetAdmin only; the service re-checks.
export async function approveUser(id) {
  if (USE_MOCK && !API_BASE_URL) return { id, status: "approved", role_applied: true };
  const headers = await authHeaders({ "Content-Type": "application/json" });
  const res = await fetch(`${API_BASE_URL}/users/approve`, {
    method: "POST",
    headers,
    body: JSON.stringify({ id }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message || `POST /users/approve → ${res.status}`);
  return data;
}

export async function updateUserRole(email, role) {
  if (USE_MOCK) return { email, previous_role: null, current_role: role };
  const encodedEmail = encodeURIComponent(email);
  // The access token, not the id token authHeaders() supplies: this endpoint
  // authorizes the caller via cognito.get_user, which rejects id tokens outright.
  const token = await getAccessToken();
  const res = await fetch(`${MFA_AUTH_BASE}/users/${encodedEmail}/role`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ new_role: role }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message || `PATCH /users/${encodedEmail}/role → ${res.status}`);
  return data;
}

// ── Segments ───────────────────────────────────────────────────────────────────
export async function getSegments() {
  if (USE_MOCK || USE_MOCK_SEGMENTS) return MOCK_SEGMENTS;
  return apiGet("/admin/segments");
}

export async function createSegment(payload) {
  if (USE_MOCK) return { id: "seg-new-" + Date.now(), ...payload };
  return apiPost("/admin/segments", payload);
}

// ── System Health ──────────────────────────────────────────────────────────────
export async function getSystemStatus() {
  try {
    const token = await getAccessToken();
    const response = await axios.get(`${MFA_AUTH_BASE}/system-status`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return response?.data ?? null;
  } catch (err) {
    handleUnauthorizedSession(err);
    console.error("[SystemStatus] fetch failed —", err?.message || err);
    return null;
  }
}

// ── Notifications ──────────────────────────────────────────────────────────────
// getAccessToken, not authHeaders: both notification Lambdas identify the caller
// with cognito.get_user, which rejects an id token — see the note above
// getAccessToken. The explicit-fetch shape (rather than apiGet/apiPost) is what
// surfaces the Lambda's `message` field to the UI.

/**
 * Fetch notifications newer than `since`, the cursor from a previous response.
 * GET /notifications/inbox
 *
 * Called on every WebSocket (re)connect: the socket only delivers while it is
 * open, so this is what fills the gaps. Omitting `since` returns the last 24h.
 * Resolves with { recipient, count, notifications, next_since }.
 */
export async function apiGetInbox({ since, limit } = {}) {
  const token = await getAccessToken();
  const qs = new URLSearchParams();
  if (since) qs.set("since", since);
  if (limit) qs.set("limit", String(limit));
  const suffix = qs.toString() ? `?${qs}` : "";

  const res = await fetch(`${MFA_AUTH_BASE}/notifications/inbox${suffix}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message || `Failed to load notifications (${res.status})`);
  return data;
}

/**
 * Send a notification to one or more users.
 * POST /notifications/send with { emails, header, content }.
 *
 * Pass `email` for a single address or `emails` for many — the Lambda accepts
 * both and authorises the whole batch once, which is why sending to 20 people is
 * one request rather than 20.
 *
 * Resolves with { stored, sent, failed, delivered, stale, recipients }.
 * `stored: true` means every recipient will receive it; `delivered: 0` is normal
 * and means nobody has a tab open right now — not a failure. A 207 resolves
 * rather than throwing, with the addresses that failed listed in `failed`, so a
 * partial send is reported instead of being mistaken for a total one. Targeting
 * any address other than your own requires the FleetAdmin group and 403s
 * otherwise.
 */
export async function apiSendNotification({ email, emails, header, content, link, icon, data }) {
  const token = await getAccessToken();
  const recipients = emails ?? (email ? [email] : []);
  const res = await fetch(`${MFA_AUTH_BASE}/notifications/send`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      emails: recipients,
      header,
      content,
      ...(link ? { link } : {}),
      ...(icon ? { icon } : {}),
      ...(data ? { data } : {}),
    }),
  });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(payload.message || `Send notification failed (${res.status})`);
  return payload;
}

/**
 * List the users who currently have the portal open.
 * GET /notifications/online-users — FleetAdmin only.
 *
 * Presence comes from the WebSocket connection table, so this is who a
 * notification would reach live. An empty list is a normal answer, not a
 * failure — it means nobody has a tab open. Sending to an offline user still
 * works; the notification waits in their inbox.
 *
 * Resolves with { users, count }, each user
 * { email, fullname, role, connections, since }.
 */
export async function getOnlineUsers() {
  const token = await getAccessToken();
  const res = await fetch(`${MFA_AUTH_BASE}/notifications/online-users`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message || `Failed to load online users (${res.status})`);
  return { users: data.users ?? [], count: data.count ?? (data.users?.length ?? 0) };
}
